import ApiService from "@/core/services/ApiService";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Module, Action, Mutation, VuexModule } from "vuex-module-decorators";

// 卡牌接口
export interface Card {
  id: number;
  card_series_id: number;
  card_series?: {
    id: number;
    name: string;
  };
  card_rarity_id: number;
  card_rarity?: {
    id: number;
    name: string;
  };
  card_language_id: number | null;
  card_language?: {
    id: number;
    name: string;
  };
  card_version_id: number | null;
  card_version?: {
    id: number;
    name: string;
  };
  product_id: number | null;
  product?: {
    id: number;
    name: string;
    recycle_price: number;
  };
  name: string;
  rarity: string;
  rarity_display?: string;
  language?: string;
  version?: string;
  image: string | null;
  description: string | null;
  recycle_price: number;
  sort: number;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  updated_by?: string;
}

// 下拉选项接口
export interface CardOption {
  id: number;
  name: string;
  rarity: string;
  rarity_display?: string;
  image: string | null;
  card_language_id?: number | null;
  card_language_name?: string | null;
  card_version_id?: number | null;
  card_version_name?: string | null;
}

// 稀有度选项接口（用于下拉框）
export interface CardRarityOption {
  value: number;
  label: string;
}

@Module
export default class CardModule extends VuexModule {
  cards_list = [] as Array<Card>;
  cards_count = 0;
  card = {} as Card;
  all_cards = [] as Array<CardOption>;
  card_rarities = [] as Array<CardRarityOption>;
  download_cards = null as Blob | null;

  get currentCardsList(): Array<Card> {
    return this.cards_list;
  }

  get currentCard(): Card {
    return this.card;
  }

  get currentCardsCount(): number {
    return this.cards_count;
  }

  get currentAllCards(): Array<CardOption> {
    return this.all_cards;
  }

  get currentCardRarities(): Array<CardRarityOption> {
    return this.card_rarities;
  }

  get currentDownloadCards(): Blob | null {
    return this.download_cards;
  }

  @Mutation
  [Mutations.SET_CARDS_LIST](cards_list: Array<Card>) {
    this.cards_list = cards_list;
  }

  @Mutation
  [Mutations.SET_CARD](card: Card) {
    this.card = card;
  }

  @Mutation
  [Mutations.SET_CARDS_COUNT](count: number) {
    this.cards_count = count;
  }

  @Mutation
  [Mutations.SET_ALL_CARDS](all_cards: Array<CardOption>) {
    this.all_cards = all_cards;
  }

  @Mutation
  [Mutations.SET_CARD_RARITIES](rarities: Array<CardRarityOption>) {
    this.card_rarities = rarities;
  }

  @Mutation
  [Mutations.SET_DOWNLOAD_CARDS](data: Blob) {
    this.download_cards = data;
  }

  @Action
  [Actions.GET_CARDS](params: Record<string, unknown>) {
    ApiService.setHeader();

    let queryString = "?";
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          queryString += "&" + key + "=" + params[key];
        }
      });
    }

    return new Promise<void>((resolve, reject) => {
      ApiService.get("admin/cards", queryString)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_CARDS_LIST, data["cards"] || []);
          this.context.commit(Mutations.SET_CARDS_COUNT, data["total_result"] || 0);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  @Action
  [Actions.GET_CARD](id: number) {
    ApiService.setHeader();

    return new Promise<void>((resolve, reject) => {
      ApiService.get(`admin/cards/${id}`)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_CARD, data["card"]);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  @Action
  [Actions.CREATE_CARD](params: Record<string, unknown>) {
    ApiService.setHeader();

    return new Promise<{ success: boolean; error_msg?: string }>((resolve, reject) => {
      ApiService.post("admin/cards", { card: params })
        .then(({ data }) => {
          if (data.success) {
            this.context.commit(Mutations.SET_CARD, data["card"]);
          }
          resolve(data);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject(response?.data);
        });
    });
  }

  @Action
  [Actions.UPDATE_CARD](credentials: { id: number; values: Record<string, unknown> }) {
    ApiService.setHeader();

    return new Promise<{ success: boolean; error_msg?: string }>((resolve, reject) => {
      ApiService.put(`admin/cards/${credentials.id}`, { card: credentials.values })
        .then(({ data }) => {
          if (data.success) {
            this.context.commit(Mutations.SET_CARD, data["card"]);
          }
          resolve(data);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject(response?.data);
        });
    });
  }

  @Action
  [Actions.DELETE_CARD](id: number) {
    ApiService.setHeader();

    return new Promise<{ success: boolean; error_msg?: string }>((resolve, reject) => {
      ApiService.delete(`admin/cards/${id}`)
        .then(({ data }) => {
          resolve(data);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject(response?.data);
        });
    });
  }

  @Action
  [Actions.GET_ALL_CARDS](params?: { card_series_id?: number }) {
    ApiService.setHeader();

    let queryString = "";
    if (params?.card_series_id) {
      queryString = `?card_series_id=${params.card_series_id}`;
    }

    return new Promise<void>((resolve, reject) => {
      ApiService.get("admin/cards/all", queryString)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_ALL_CARDS, data["cards"] || []);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  @Action
  [Actions.GET_CARD_RARITIES]() {
    ApiService.setHeader();

    return new Promise<void>((resolve, reject) => {
      ApiService.get("admin/cards/index_rarities")
        .then(({ data }) => {
          this.context.commit(Mutations.SET_CARD_RARITIES, data["rarities"] || []);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  @Action
  [Actions.DOWNLOAD_CARDS](params?: Record<string, unknown>) {
    ApiService.setHeader();

    let queryString = "?";
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          queryString += "&" + key + "=" + params[key];
        }
      });
    }

    return new Promise<void>((resolve, reject) => {
      ApiService.get("admin/cards/download_excel", queryString, { responseType: 'blob' })
        .then(({ data }) => {
          this.context.commit(Mutations.SET_DOWNLOAD_CARDS, data);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '下载失败');
          reject();
        });
    });
  }

  @Action
  [Actions.UPLOAD_CARDS](params: { file: File }) {
    ApiService.setHeader();

    const formData = new FormData();
    formData.append('file', params.file);

    return new Promise<{ success: boolean; error_msg?: string; imported_count?: number }>((resolve, reject) => {
      ApiService.post_multipart("admin/cards/upload", formData)
        .then(({ data }) => {
          resolve(data);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '上传失败');
          reject(response?.data);
        });
    });
  }

  @Action
  [Actions.UPLOAD_CARD_IMAGES](params: {
    file: File;
    card_language_id?: number | string;
    card_version_id?: number | string;
    overwrite?: boolean;
  }) {
    ApiService.setHeader();
    const formData = new FormData();
    formData.append('file', params.file);
    if (params.card_language_id) formData.append('card_language_id', String(params.card_language_id));
    if (params.card_version_id) formData.append('card_version_id', String(params.card_version_id));
    formData.append('overwrite', params.overwrite ? 'true' : 'false');
    return new Promise<any>((resolve, reject) => {
      ApiService.post_multipart('admin/cards/upload_images', formData)
        .then(({ data }) => resolve(data))
        .catch(({ response }) => reject(response?.data));
    });
  }

  @Action
  [Actions.GET_CARD_IMAGES_UPLOAD_STATUS](jobId: string) {
    ApiService.setHeader();
    return new Promise<any>((resolve, reject) => {
      ApiService.get('admin/cards/upload_images_status', `?job_id=${jobId}`)
        .then(({ data }) => resolve(data))
        .catch(({ response }) => reject(response?.data));
    });
  }
}
